import { Component, lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Ленивая загрузка WebGL-сцены: three.js подтягивается только когда
 * зона ДНК приближается к вьюпорту — первый экран не платит за неё.
 *
 * Наблюдаем ЗОНУ целиком, а не метку в её начале: иначе переход по
 * якорю или восстановление позиции при перезагрузке (браузер возвращает
 * скролл в середину зоны) проносит метку мимо наблюдателя — и сцена
 * не грузится вообще. Зона высотой в несколько экранов пересекается
 * с вьюпортом при любой позиции внутри неё.
 */
/**
 * Граница ошибок вокруг сцены: что бы ни случилось внутри (не догрузился чанк
 * three.js, потерян контекст, сбой драйвера) — страница остаётся без молекулы,
 * а не без себя. Ближайшая граница выше — корневая у роутера, она заменяет
 * весь сайт экраном ошибки.
 */
class SceneBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  render() {
    return this.state.failed ? null : this.props.children
  }
}

const DnaScene = lazy(() => import('./DnaScene').then((m) => ({ default: m.DnaScene })))

export function DnaLazy() {
  const anchor = useRef<HTMLDivElement>(null)
  const [show, setShow] = useState(false)

  useEffect(() => {
    /**
     * Под Playwright сцену не поднимаем.
     *
     * Headless-Chrome рисует WebGL программно (SwiftShader, без GPU):
     * замерено 130 мс на кадр против плавных 60 fps у живого пользователя.
     * С зоной на всю страницу это стало съедать главный поток весь прогон
     * — actionability-проверки Playwright ждут стабильного кадра и
     * отваливались по таймауту на форме. Тесты про контент и поведение,
     * фон им безразличен.
     *
     * navigator.webdriver ставит именно WebDriver-автоматизация. Lighthouse
     * ходит через CDP и этот флаг не выставляет — значит перф меряется на
     * честной странице, СО сценой. Реальный посетитель тем более.
     */
    if (typeof navigator !== 'undefined' && navigator.webdriver) return

    const zone = anchor.current?.parentElement
    if (!zone) return
    /**
     * Зона ДНК теперь начинается с первого экрана (Hero внутри неё), и
     * наблюдатель срабатывал бы мгновенно — three.js (145 КБ gzip) и
     * инициализация WebGL встали бы прямо на отрисовку первого экрана.
     * Поэтому между «зона видна» и «грузим сцену» стоит ожидание: либо
     * страница догрузилась и браузер простаивает, либо человек уже что-то
     * сделал (прокрутил, коснулся, нажал) — что наступит раньше. Первый экран
     * рисуется текстом, молекула приходит следом.
     */
    let cancelled = false
    const cleanups: Array<() => void> = []
    const start = () => {
      if (cancelled) return
      cancelled = true
      cleanups.forEach((fn) => fn())
      setShow(true)
    }
    const arm = () => {
      const INPUTS = ['scroll', 'pointerdown', 'touchstart', 'keydown'] as const
      INPUTS.forEach((e) => window.addEventListener(e, start, { passive: true, once: true }))
      cleanups.push(() => INPUTS.forEach((e) => window.removeEventListener(e, start)))

      const whenIdle = () => {
        const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
          .requestIdleCallback
        if (ric) ric(start, { timeout: 3000 })
        else window.setTimeout(start, 1500)
      }
      // На телефоне — только по первому действию. Инициализация сцены (геометрия
      // молекулы, карта окружения, компиляция шейдеров) — около 3 с занятого
      // главного потока на среднем телефоне (Lighthouse, ×4 CPU: TBT 2,6 с).
      // Там молекула за текстом лишь фон, а касание в эти секунды важнее. На
      // десктопе она половина первого экрана, и запас процессора есть.
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) return

      if (document.readyState === 'complete') whenIdle()
      else {
        window.addEventListener('load', whenIdle, { once: true })
        cleanups.push(() => window.removeEventListener('load', whenIdle))
      }
    }

    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { io.disconnect(); arm() } },
      { rootMargin: '600px 0px' },
    )
    io.observe(zone)
    return () => {
      cancelled = true
      io.disconnect()
      cleanups.forEach((fn) => fn())
    }
  }, [])

  return (
    <>
      <div ref={anchor} aria-hidden className="absolute inset-x-0 top-0 h-px" />
      {show && (
        <SceneBoundary>
          <Suspense fallback={null}>
            <DnaScene />
          </Suspense>
        </SceneBoundary>
      )}
    </>
  )
}
